import { SegmentPageContent } from "@/types/marketing/segment-page";

export const SALES_LEAD_CAPTURE_PAGE: SegmentPageContent = {
  slug: "sales-lead-capture",
  title: "Sales & Lead Capture",
  audienceLabel: "What You Can Do",
  description:
    "Turn every conversation into a qualified lead with voice-powered sales and capture flows.",
  seoDescription:
    "Use Help Genie to capture, qualify, and convert leads 24/7 with voice AI agents that handle inbound enquiries, qualify prospects, and book meetings automatically.",
  heroHeadline: "Never miss a",
  heroHighlightWord: "lead",
  heroRotatingWords: ["lead", "sale", "enquiry", "prospect"],
  heroSuffix: "again",
  heroSubheadline:
    "Voice agents that qualify leads, answer sales questions, and book meetings, around the clock, without missing a single opportunity.",
  primaryCTA: {
    label: "Start Free Trial",
    href: "/signup",
  },
  secondaryCTA: {
    label: "Try a Demo",
    href: "/live-agent/business-onboarding",
  },
  capabilityTitle: "Built for revenue teams",
  capabilitySubtitle:
    "Everything you need to capture, qualify, and convert leads through natural voice conversations.",
  capabilities: [
    {
      title: "24/7 Lead Qualification",
      description:
        "Never miss an inbound lead again. Your voice agent handles enquiries around the clock, asks the right qualifying questions, and captures contact details naturally, so your team wakes up to warm leads, not missed calls.",
      image: "/segment-pages/sales-lead-capture/cap-247-qualification.webp",
    },
    {
      title: "Conversational Sales Discovery",
      description:
        "Let prospects explore your products and services through natural conversation. Your agent answers pricing questions, explains features, and guides buyers toward the right solution, all from your knowledge base.",
      image: "/segment-pages/sales-lead-capture/cap-sales-discovery.webp",
    },
    {
      title: "Smart Routing & Handoff",
      description:
        "When a conversation needs a human touch, your agent transfers with full context attached. Route high-intent prospects to the right sales rep based on product interest, deal size, or territory.",
      image: "/segment-pages/sales-lead-capture/cap-smart-routing.webp",
    },
    {
      title: "Pipeline Intelligence",
      description:
        "Every conversation generates insights. Track which questions prospects ask, what objections come up, and where leads drop off, so you can continuously sharpen your sales approach.",
      image: "/segment-pages/sales-lead-capture/cap-pipeline-intelligence.webp",
    },
  ],
  useCaseTitle: "Where sales teams see immediate results",
  useCaseSubtitle:
    "Real revenue outcomes from voice-powered lead capture and qualification.",
  useCases: [
    {
      title: "After-Hours Lead Capture",
      description:
        "Capture leads that arrive outside business hours, evenings, weekends, and holidays, without missing a beat.",
      bullets: [
        "24/7 availability without staffing costs",
        "Automatic lead scoring and prioritisation",
        "Instant email notifications for hot leads",
      ],
      image: "/segment-pages/sales-lead-capture/usecase-after-hours.webp",
    },
    {
      title: "Website Visitor Conversion",
      description:
        "Engage website visitors with proactive voice conversations that convert browsers into buyers.",
      bullets: [
        "Embedded voice widget on any page",
        "Contextual conversations based on page content",
        "Progressive lead profiling across sessions",
      ],
      image: "/segment-pages/sales-lead-capture/usecase-website-conversion.webp",
    },
    {
      title: "Event & Campaign Follow-Up",
      description:
        "Share a QR code or link at events and let your voice agent handle the follow-up conversation immediately.",
      bullets: [
        "QR code to instant voice conversation",
        "Campaign-specific knowledge and messaging",
        "Automated follow-up emails with transcripts",
      ],
      image: "/segment-pages/sales-lead-capture/usecase-event-followup.webp",
    },
  ],
  processTitle: "From visitor to qualified lead in three steps",
  processSubtitle:
    "Set up a sales-focused voice agent in minutes, not weeks.",
  processSteps: [
    {
      title: "Train Your Sales Agent",
      description:
        "Upload your product info, pricing, FAQs, and sales playbook. Your agent learns your pitch instantly.",
    },
    {
      title: "Deploy Across Channels",
      description:
        "Add your agent to your website, share via links or QR codes, or connect a phone number for inbound calls.",
    },
    {
      title: "Capture & Convert",
      description:
        "Leads are qualified, scored, and delivered to your inbox or CRM, with full conversation context attached.",
    },
  ],
  pillars: [
    {
      title: "Qualify Leads",
      description:
        "Ask the right questions and score prospects automatically through natural conversation.",
      gradient: "from-purple-400 to-blue-400",
    },
    {
      title: "Book Meetings",
      description:
        "Guide qualified prospects to schedule time with your sales team, hands-free.",
      gradient: "from-blue-400 to-cyan-400",
    },
    {
      title: "Close Faster",
      description:
        "Arm your reps with full conversation context so they can pick up exactly where the agent left off.",
      gradient: "from-cyan-400 to-purple-400",
    },
  ],
  finalCTAHeadline: "Stop losing leads to voicemail",
  finalCTACopy:
    "Every missed call is a missed opportunity. Deploy a voice agent that captures, qualifies, and routes leads 24/7, so your sales team can focus on closing.",
  finalPrimaryCTA: {
    label: "Start Free Trial",
    href: "/signup",
  },
  finalSecondaryCTA: {
    label: "See Playbooks",
    href: "/playbooks",
  },
  imagery: {
    heroImage: "/segment-pages/sales-lead-capture/hero.webp",
    ctaBackgroundImage: "/segment-pages/sales-lead-capture/cta-background.webp",
  },
};

export const INTERNAL_INFORMATION_DELIVERY_PAGE: SegmentPageContent = {
  slug: "internal-information-delivery",
  title: "Internal Information Delivery",
  audienceLabel: "What You Can Do",
  description:
    "Give your team instant voice access to the information they need, when they need it.",
  seoDescription:
    "Use Help Genie to create internal voice agents that deliver company knowledge, onboard staff, answer policy questions, and reduce repetitive internal enquiries.",
  heroHeadline: "Your team gets",
  heroHighlightWord: "answers",
  heroRotatingWords: ["answers", "clarity", "guidance", "access"],
  heroSuffix: "instantly",
  heroSubheadline:
    "Voice agents that deliver company knowledge to your team on demand, from HR policies and SOPs to product specs and onboarding guides.",
  primaryCTA: {
    label: "Start Free Trial",
    href: "/signup",
  },
  secondaryCTA: {
    label: "Try a Demo",
    href: "/live-agent/business-onboarding",
  },
  capabilityTitle: "Your company knowledge, always available",
  capabilitySubtitle:
    "Reduce internal tickets, speed up onboarding, and make sure your team always has the right answer.",
  capabilities: [
    {
      title: "Instant Knowledge Access",
      description:
        "Upload your SOPs, handbooks, product documentation, and internal guides. Your team can ask questions in natural language and get accurate answers instantly, no more searching through folders or waiting for someone to reply.",
      image: "/segment-pages/internal-information-delivery/cap-knowledge-access.webp",
    },
    {
      title: "Staff Onboarding & Training",
      description:
        "New hires get a personal onboarding agent that walks them through company processes, answers their questions, and helps them get up to speed, without pulling senior staff away from their work.",
      image: "/segment-pages/internal-information-delivery/cap-onboarding.webp",
    },
    {
      title: "Policy & Compliance Guidance",
      description:
        "Make sure your team always has access to the latest policies, safety procedures, and compliance requirements. Your agent stays up to date as you update your knowledge base.",
      image: "/segment-pages/internal-information-delivery/cap-policy-compliance.webp",
    },
    {
      title: "Multi-Department Coverage",
      description:
        "Create dedicated agents for HR, IT, operations, or any department. Each one trained on the right knowledge and accessible to the right people.",
      image: "/segment-pages/internal-information-delivery/cap-multi-department.webp",
    },
  ],
  useCaseTitle: "Where teams see immediate impact",
  useCaseSubtitle:
    "Real productivity gains from voice-powered internal knowledge delivery.",
  useCases: [
    {
      title: "HR & People Operations",
      description:
        "Answer leave policies, benefits questions, and onboarding steps without HR staff needing to repeat themselves.",
      bullets: [
        "Leave and benefits policy lookups",
        "Onboarding checklists and guides",
        "Company handbook access via voice",
      ],
      image: "/segment-pages/internal-information-delivery/usecase-hr.webp",
    },
    {
      title: "IT & Technical Support",
      description:
        "Help staff troubleshoot common issues, find documentation, and follow technical procedures without waiting for a ticket response.",
      bullets: [
        "Self-service troubleshooting guides",
        "Software setup and configuration help",
        "Password and access request workflows",
      ],
      image: "/segment-pages/internal-information-delivery/usecase-it.webp",
    },
    {
      title: "Field & Remote Teams",
      description:
        "Give mobile and remote workers instant voice access to procedures, specs, and documentation, even when they can't search a computer.",
      bullets: [
        "Voice-first access for hands-free use",
        "Technical specs and procedure lookups",
        "Safety and compliance checklists",
      ],
      image: "/segment-pages/internal-information-delivery/usecase-field.webp",
    },
  ],
  processTitle: "Three steps to smarter internal knowledge",
  processSubtitle:
    "Deploy an internal knowledge agent in minutes, no IT project required.",
  processSteps: [
    {
      title: "Upload Your Knowledge",
      description:
        "Add your handbooks, SOPs, FAQs, and documentation. Your agent learns your company knowledge instantly.",
    },
    {
      title: "Set Access & Permissions",
      description:
        "Control who can access which agent. Share via internal links, embed on your intranet, or connect to a phone line.",
    },
    {
      title: "Answer Questions Instantly",
      description:
        "Your team asks questions in natural language and gets accurate answers, reducing internal tickets and wait times.",
    },
  ],
  pillars: [
    {
      title: "Reduce Tickets",
      description:
        "Deflect repetitive internal questions so your support teams can focus on complex issues.",
      gradient: "from-purple-400 to-blue-400",
    },
    {
      title: "Onboard Faster",
      description:
        "New hires get instant answers without pulling senior staff away from their work.",
      gradient: "from-blue-400 to-cyan-400",
    },
    {
      title: "Stay Consistent",
      description:
        "Everyone gets the same accurate, up-to-date information, no more outdated documents or hallway answers.",
      gradient: "from-cyan-400 to-purple-400",
    },
  ],
  finalCTAHeadline: "Your team deserves instant answers",
  finalCTACopy:
    "Stop wasting hours searching for information or waiting for internal replies. Deploy a voice agent that gives your team the knowledge they need, exactly when they need it.",
  finalPrimaryCTA: {
    label: "Start Free Trial",
    href: "/signup",
  },
  finalSecondaryCTA: {
    label: "View Solutions",
    href: "/help-genie-for-business-solutions",
  },
  imagery: {
    heroImage: "/segment-pages/internal-information-delivery/hero.webp",
    ctaBackgroundImage: "/segment-pages/internal-information-delivery/cta-background.webp",
  },
};

export const PRODUCT_SUPPORT_PAGE: SegmentPageContent = {
  slug: "product-support",
  title: "Product Support",
  audienceLabel: "What You Can Do",
  description:
    "Deliver instant, accurate product support through voice conversations, 24/7.",
  seoDescription:
    "Use Help Genie to provide 24/7 voice-powered product support that answers customer questions, troubleshoots issues, and reduces support ticket volume.",
  heroHeadline: "Support that",
  heroHighlightWord: "scales",
  heroRotatingWords: ["scales", "delivers", "delights", "resolves"],
  heroSuffix: "",
  heroSubheadline:
    "Voice agents trained on your product knowledge that answer customer questions, troubleshoot issues, and escalate when needed, without growing your support team.",
  primaryCTA: {
    label: "Start Free Trial",
    href: "/signup",
  },
  secondaryCTA: {
    label: "Try a Demo",
    href: "/live-agent/business-onboarding",
  },
  capabilityTitle: "Product support that works around the clock",
  capabilitySubtitle:
    "Answer questions, resolve issues, and keep customers happy, without scaling your team.",
  capabilities: [
    {
      title: "Instant Product Answers",
      description:
        "Customers ask questions about your products and get accurate, detailed answers immediately. Your agent draws from your product documentation, manuals, and FAQs to deliver consistent, reliable support.",
      image: "/segment-pages/product-support/cap-instant-answers.webp",
    },
    {
      title: "Guided Troubleshooting",
      description:
        "Walk customers through diagnostic steps and troubleshooting procedures via voice conversation. Your agent follows your support playbooks to resolve issues methodically.",
      image: "/segment-pages/product-support/cap-troubleshooting.webp",
    },
    {
      title: "Smart Escalation",
      description:
        "When an issue needs human attention, your agent escalates with full context, what the customer asked, what was tried, and what needs to happen next. No repeat explanations required.",
      image: "/segment-pages/product-support/cap-escalation.webp",
    },
    {
      title: "Support Analytics",
      description:
        "Understand what customers are asking about most, which issues are trending, and where your documentation has gaps, so you can continuously improve your product and support.",
      image: "/segment-pages/product-support/cap-support-analytics.webp",
    },
  ],
  useCaseTitle: "Where product support teams see results",
  useCaseSubtitle:
    "Real outcomes from voice-powered support across industries.",
  useCases: [
    {
      title: "Tier-1 Ticket Deflection",
      description:
        "Handle common support questions automatically, freeing your team to focus on complex, high-value issues.",
      bullets: [
        "Up to 60% reduction in ticket volume",
        "Instant answers from your knowledge base",
        "Seamless escalation for complex issues",
      ],
      image: "/segment-pages/product-support/usecase-ticket-deflection.webp",
    },
    {
      title: "Product Setup & Onboarding",
      description:
        "Guide new customers through product setup, configuration, and first-use experiences with voice-guided walkthroughs.",
      bullets: [
        "Step-by-step setup guidance",
        "Configuration assistance",
        "First-use best practices",
      ],
      image: "/segment-pages/product-support/usecase-product-setup.webp",
    },
    {
      title: "Warranty & Returns",
      description:
        "Handle warranty checks, return eligibility, and RMA processes through natural conversation, reducing call centre load.",
      bullets: [
        "Automated warranty validation",
        "Return eligibility checks",
        "RMA process guidance",
      ],
      image: "/segment-pages/product-support/usecase-warranty.webp",
    },
  ],
  processTitle: "Deploy product support in three steps",
  processSubtitle:
    "Get your support agent live in minutes, trained on your entire product knowledge base.",
  processSteps: [
    {
      title: "Add Product Knowledge",
      description:
        "Upload manuals, FAQs, troubleshooting guides, and product specs. Your agent learns everything about your products.",
    },
    {
      title: "Configure Support Flows",
      description:
        "Set up escalation rules, lead capture for warranty claims, and custom greetings for different products or channels.",
    },
    {
      title: "Go Live Everywhere",
      description:
        "Embed on your support pages, connect a phone number, or share via QR codes on products and packaging.",
    },
  ],
  pillars: [
    {
      title: "Deflect Tickets",
      description:
        "Resolve common issues instantly so your team can focus on what matters most.",
      gradient: "from-purple-400 to-blue-400",
    },
    {
      title: "Delight Customers",
      description:
        "Instant, accurate answers that make customers feel supported, day or night.",
      gradient: "from-blue-400 to-cyan-400",
    },
    {
      title: "Learn & Improve",
      description:
        "Every conversation reveals what customers need, so your product and support get better over time.",
      gradient: "from-cyan-400 to-purple-400",
    },
  ],
  finalCTAHeadline: "Better support without more headcount",
  finalCTACopy:
    "Your customers expect instant answers. Your team needs to focus on complex issues. Deploy a voice agent that handles the rest, 24/7, with your product knowledge built in.",
  finalPrimaryCTA: {
    label: "Start Free Trial",
    href: "/signup",
  },
  finalSecondaryCTA: {
    label: "See Playbooks",
    href: "/playbooks",
  },
  imagery: {
    heroImage: "/segment-pages/product-support/hero.webp",
    ctaBackgroundImage: "/segment-pages/product-support/cta-background.webp",
  },
};

export const VOICE_EXPERIENCES_PAGE: SegmentPageContent = {
  slug: "voice-experiences",
  title: "Voice Experiences",
  audienceLabel: "What You Can Do",
  description:
    "Create unique, branded voice experiences for customers, internal teams, and stakeholders.",
  seoDescription:
    "Build custom voice experiences with Help Genie. Branded voice agents for customer engagement, team enablement, events, and stakeholder presentations. Deploy via web, phone, QR codes, and embeds.",
  heroHeadline: "Give your brand a",
  heroHighlightWord: "voice",
  heroRotatingWords: ["voice", "presence", "persona", "stage"],
  heroSuffix: "",
  heroSubheadline:
    "Create voice experiences that engage customers, empower teams, and impress stakeholders, each one branded, knowledge-powered, and deployed in minutes.",
  primaryCTA: {
    label: "Start Building",
    href: "/signup",
  },
  secondaryCTA: {
    label: "Try a Demo",
    href: "/live-agent/business-onboarding",
  },
  capabilityTitle: "One platform, every audience",
  capabilitySubtitle:
    "Build voice experiences for customers, teams, partners, and events, each one trained on your knowledge and branded to your identity.",
  capabilities: [
    {
      title: "Customer Touchpoints",
      description:
        "Embed a voice agent on your website, share it via a link from a campaign, or print a QR code on packaging. Customers talk to your brand and get real answers, about your products, pricing, availability, and more, from your own knowledge base.",
      image: "/segment-pages/voice-experiences/cap-customer-facing.webp",
    },
    {
      title: "Team Knowledge Tools",
      description:
        "Give your staff a voice-powered companion trained on SOPs, product specs, and internal policies. New hires onboard faster. Field workers get procedures hands-free. Everyone gets the same accurate, up-to-date answers without waiting for a reply.",
      image: "/segment-pages/voice-experiences/cap-internal-teams.webp",
    },
    {
      title: "Stakeholder Presentations",
      description:
        "Let investors, board members, or partners interact with a voice experience that showcases your product, results, or roadmap. It's more engaging than a deck, and it speaks to your brand's capability and innovation.",
      image: "/segment-pages/voice-experiences/cap-stakeholder.webp",
    },
    {
      title: "Events & Campaigns",
      description:
        "Launch a voice experience for a trade show, product launch, or marketing campaign. Attendees scan a QR code, tap a link, or call a number, and your brand talks back. The conversation keeps working long after the event ends.",
      image: "/segment-pages/voice-experiences/cap-events.webp",
    },
  ],
  useCaseTitle: "Voice experiences in action",
  useCaseSubtitle:
    "Real examples of how teams use Help Genie to create memorable, branded voice moments.",
  useCases: [
    {
      title: "Interactive Product Discovery",
      description:
        "Let customers explore your products through conversation instead of scrolling. Your voice agent knows your catalogue, answers comparison questions, and guides buyers toward the right choice.",
      bullets: [
        "Trained on your full product catalogue",
        "Handles pricing, specs, and availability questions",
        "Embed on product pages or share via campaign links",
      ],
      image: "/segment-pages/voice-experiences/usecase-product-showcase.webp",
    },
    {
      title: "Branded Welcome & Onboarding",
      description:
        "Greet new hires, visitors, or event attendees with a voice experience that introduces your brand, answers their first questions, and guides them to what they need, in their language.",
      bullets: [
        "Custom voice, personality, and brand identity",
        "40+ languages for global teams and audiences",
        "Deploy on tablets, kiosks, or shared links",
      ],
      image: "/segment-pages/voice-experiences/usecase-welcome.webp",
    },
    {
      title: "Voice-Powered Knowledge Portals",
      description:
        "Build portals where customers or staff access documentation, troubleshooting guides, and FAQs through natural voice conversation, no searching, no scrolling, no waiting.",
      bullets: [
        "Trained on your docs, manuals, and FAQs",
        "Accessible via web embed, phone, or QR code",
        "Conversation transcripts and analytics built in",
      ],
      image: "/segment-pages/voice-experiences/usecase-knowledge-portal.webp",
    },
  ],
  processTitle: "Three steps to a live voice experience",
  processSubtitle:
    "From idea to deployed experience, no developers needed.",
  processSteps: [
    {
      title: "Upload & Train",
      description:
        "Add your product docs, FAQs, policies, or any knowledge your experience needs. Choose a voice and set the personality.",
    },
    {
      title: "Brand & Configure",
      description:
        "Apply your colours, logo, and messaging. Set conversation goals, lead capture, and post-conversation follow-ups.",
    },
    {
      title: "Deploy & Share",
      description:
        "Go live via website embed, direct link, QR code, or phone number. Share with customers, teams, or stakeholders instantly.",
    },
  ],
  pillars: [
    {
      title: "Engage\nCustomers",
      description:
        "Branded touchpoints that turn browsers into buyers and visitors into fans.",
      gradient: "from-purple-400 to-blue-400",
    },
    {
      title: "Empower\nTeams",
      description:
        "Voice tools that put company knowledge at your team's fingertips, hands-free.",
      gradient: "from-blue-400 to-cyan-400",
    },
    {
      title: "Impress\nStakeholders",
      description:
        "Interactive presentations that let your brand speak for itself.",
      gradient: "from-cyan-400 to-purple-400",
    },
  ],
  finalCTAHeadline: "Your brand deserves a voice",
  finalCTACopy:
    "Every interaction is a chance to engage, inform, and impress. Build voice experiences that bring your brand to life, for customers, teams, and everyone in between.",
  finalPrimaryCTA: {
    label: "Start Building Free",
    href: "/signup",
  },
  finalSecondaryCTA: {
    label: "View Solutions",
    href: "/help-genie-for-business-solutions",
  },
  imagery: {
    heroImage: "/segment-pages/voice-experiences/hero.webp",
    ctaBackgroundImage: "/segment-pages/voice-experiences/cta-background.webp",
  },
};
